/**
 * Analysis Service
 * Handles all infrastructure analysis operations
 *
 * @module AnalysisService
 * Sprint 32: OOA/OOD Refactoring
 */

import {
    InfrastructureReport,
    Anomaly,
    SecurityFinding,
    CostRecommendation,
    Result,
    Severity
} from '../types/ii-agent-types';
import { AnalysisHistory } from '../utils/analysis-history';
import { showNotification } from '../utils/notification-utils';

/**
 * Interface for analysis service
 */
export interface IAnalysisService {
    analyzeInfrastructure(): Promise<InfrastructureReport>;
    analyzeInfrastructureByLocation(location: string): Promise<InfrastructureReport>;
    detectAnomalies(threshold?: number): Promise<Anomaly[]>;
    auditSecurity(): Promise<SecurityFinding[]>;
    recommendCostOptimizations(): Promise<CostRecommendation[]>;
    getAllFindings(): Promise<any[]>;
    getFindingsBySeverity(severity: Severity): Promise<any[]>;
    getAllRecommendations(): Promise<any[]>;
    getStats(): Promise<any[]>;
}

/**
 * Service for infrastructure analysis operations
 */
export class AnalysisService implements IAnalysisService {
    private client: any;
    private history: AnalysisHistory;

    constructor(client: any, history: AnalysisHistory) {
        this.client = client;
        this.history = history;
    }

    /**
     * Get infrastructure analysis
     */
    async analyzeInfrastructure(): Promise<InfrastructureReport> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            showNotification('Analyzing infrastructure...', 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'analyze_infrastructure',
                []
            ) as Result<InfrastructureReport, string>;

            if (result && 'Ok' in result) {
                const report = result.Ok!;
                this.history.add({
                    type: 'infrastructure',
                    timestamp: Date.now(),
                    data: report
                });
                showNotification('Infrastructure analysis complete', 'success');
                return report;
            } else {
                throw new Error('Err' in result ? result.Err : 'Analysis failed');
            }
        } catch (error) {
            console.error('Error analyzing infrastructure:', error);
            showNotification('Infrastructure analysis failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Get infrastructure analysis filtered by location
     */
    async analyzeInfrastructureByLocation(location: string): Promise<InfrastructureReport> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            showNotification(`Analyzing infrastructure in ${location}...`, 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'analyze_infrastructure_by_location',
                [location]
            ) as Result<InfrastructureReport, string>;

            if (result && 'Ok' in result) {
                const report = result.Ok!;
                this.history.add({
                    type: 'infrastructure',
                    timestamp: Date.now(),
                    location: location,
                    data: report
                });
                showNotification(`Infrastructure analysis for ${location} complete`, 'success');
                return report;
            } else {
                throw new Error('Err' in result ? result.Err : 'Analysis failed');
            }
        } catch (error) {
            console.error('Error analyzing infrastructure by location:', error);
            showNotification('Infrastructure analysis failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Detect anomalies in infrastructure
     */
    async detectAnomalies(threshold: number = 0.5): Promise<Anomaly[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            showNotification(`Detecting anomalies (threshold: ${threshold})...`, 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'detect_anomalies',
                [threshold]
            ) as Result<Anomaly[], string>;

            if (result && 'Ok' in result) {
                const anomalies = result.Ok!;
                this.history.add({
                    type: 'anomalies',
                    timestamp: Date.now(),
                    data: anomalies,
                    threshold
                });
                showNotification(`Found ${anomalies.length} anomalies`, 'success');
                return anomalies;
            } else {
                throw new Error('Err' in result ? result.Err : 'Anomaly detection failed');
            }
        } catch (error) {
            console.error('Error detecting anomalies:', error);
            showNotification('Anomaly detection failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Audit security compliance
     */
    async auditSecurity(): Promise<SecurityFinding[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            showNotification('Auditing security compliance...', 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'audit_security_compliance',
                []
            ) as Result<SecurityFinding[], string>;

            if (result && 'Ok' in result) {
                const findings = result.Ok!;
                this.history.add({
                    type: 'security',
                    timestamp: Date.now(),
                    data: findings
                });
                showNotification(
                    `Found ${findings.length} security findings`,
                    findings.length > 0 ? 'warning' : 'success'
                );
                return findings;
            } else {
                throw new Error('Err' in result ? result.Err : 'Security audit failed');
            }
        } catch (error) {
            console.error('Error auditing security:', error);
            showNotification('Security audit failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Get cost optimization recommendations
     */
    async recommendCostOptimizations(): Promise<CostRecommendation[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }

            showNotification('Analyzing cost optimization opportunities...', 'info');

            const result = await this.client.callCanister(
                'ii_agent',
                'recommend_cost_optimizations',
                []
            ) as Result<CostRecommendation[], string>;

            if (result && 'Ok' in result) {
                const recommendations = result.Ok!;
                this.history.add({
                    type: 'cost',
                    timestamp: Date.now(),
                    data: recommendations
                });
                showNotification(`Found ${recommendations.length} cost optimization opportunities`, 'success');
                return recommendations;
            } else {
                throw new Error('Err' in result ? result.Err : 'Cost analysis failed');
            }
        } catch (error) {
            console.error('Error analyzing costs:', error);
            showNotification('Cost analysis failed: ' + (error as Error).message, 'error');
            throw error;
        }
    }

    /**
     * Get all findings
     */
    async getAllFindings(): Promise<any[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }
            const result = await this.client.callCanister('ii_agent', 'get_all_findings', []);
            return result || [];
        } catch (error) {
            console.error('Error getting findings:', error);
            return [];
        }
    }

    /**
     * Get findings by severity
     */
    async getFindingsBySeverity(severity: Severity): Promise<any[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }
            const result = await this.client.callCanister(
                'ii_agent',
                'get_findings_by_severity',
                [{ [severity]: null }]
            );
            return result || [];
        } catch (error) {
            console.error('Error getting findings by severity:', error);
            return [];
        }
    }

    /**
     * Get all recommendations
     */
    async getAllRecommendations(): Promise<any[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }
            const result = await this.client.callCanister('ii_agent', 'get_all_recommendations', []);
            return result || [];
        } catch (error) {
            console.error('Error getting recommendations:', error);
            return [];
        }
    }

    /**
     * Get II-Agent statistics
     */
    async getStats(): Promise<any[]> {
        try {
            if (!this.client) {
                throw new Error('II-Agent not initialized');
            }
            console.log('Calling ii_agent.get_stats...');
            const result = await this.client.callCanister('ii_agent', 'get_stats', []);
            console.log('Raw result from canister:', result);
            return result || [];
        } catch (error) {
            console.error('Error getting stats:', error);
            return [];
        }
    }
}
